import { useEffect, useRef, useState, type ReactNode } from "react";
import { msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";

import type { AnimationDirection } from "./types";
import {
  IntakeMobileChromeSetterProvider,
  type IntakeMobileChromeActions,
} from "./IntakeMobileChromeContext";
import "./animations.css";

interface CardShellProps {
  children: ReactNode;
  animDir: AnimationDirection;
  progressPercent: number;
  /** Used to reset scroll position when advancing to the next card. */
  currentIndex: number;
  totalCards: number;
  isWide?: boolean;
  /** When true, vertically centers card content at all breakpoints (e.g. language picker). Default keeps mobile top-aligned for long forms. */
  verticallyCenterContent?: boolean;
  onNext: () => void;
  onBack: () => void;
  /** Unique key to force re-mount and re-trigger animation */
  cardKey: string;
}

function IntakeMobileStickyFooter({
  actions,
}: {
  actions: IntakeMobileChromeActions | null;
}) {
  const { _ } = useLingui();

  if (!actions) return null;

  const showCancel =
    actions.showCancel !== false && typeof actions.onCancel === "function";
  const showContinue =
    actions.showContinue !== false && typeof actions.onContinue === "function";
  const showSkip = typeof actions.onSkip === "function";

  if (!showCancel && !showContinue && !showSkip) return null;

  // Flex row: Back (left) + Continue (flex) + Skip (right) — matches CardActions on desktop.
  // !m-0: global index.css adds `button+button { margin-top: 8px }` on mobile.
  const cancelClassBase =
    "!m-0 box-border [touch-action:manipulation] appearance-none flex h-12 min-h-12 max-h-12 items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-0 font-medium text-sm leading-none text-gray-800 shadow-sm outline-none transition-colors duration-150 hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50";
  const cancelClass = `${cancelClassBase} shrink-0`;
  /** Back + Skip only: equal halves with gap-2 between (no Continue). */
  const cancelClassPair = `${cancelClassBase} min-w-0 flex-1 basis-0`;
  const continueClass =
    "!m-0 box-border [touch-action:manipulation] appearance-none flex h-12 min-h-12 max-h-12 items-center justify-center gap-2 rounded-lg border border-transparent bg-[#166B93] px-4 py-0 font-semibold text-sm leading-none text-white shadow-lg shadow-[#166B93]/20 outline-none transition-[background-color,opacity] duration-150 hover:bg-[#145d82] active:bg-[#125473] disabled:cursor-not-allowed disabled:opacity-50";
  const skipClassBase =
    "!m-0 box-border [touch-action:manipulation] appearance-none flex h-12 min-h-12 max-h-12 items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3.5 py-0 font-medium text-sm leading-none text-gray-800 shadow-sm outline-none transition-colors duration-150 hover:bg-gray-50 active:bg-gray-100";
  const skipClass = `${skipClassBase} shrink-0`;
  const skipClassPair = `${skipClassBase} min-w-0 flex-1 basis-0`;

  const showRow = showCancel || showContinue || showSkip;
  const backOnly = showCancel && !showContinue && !showSkip;
  const continueOnly = showContinue && !showCancel && !showSkip;
  const skipOnly = showSkip && !showCancel && !showContinue;
  const backAndSkipOnly = showCancel && showSkip && !showContinue;

  return (
    <div className="pointer-events-auto z-20 md:hidden flex-shrink-0 border-t border-gray-200/80 bg-white px-4 pt-8 shadow-[0_-4px_24px_rgba(0,0,0,0.06)] pb-[max(2rem,calc(1rem+env(safe-area-inset-bottom)))]">
      {showRow && (
        <div className="flex w-full flex-nowrap items-center gap-2">
          {showCancel && (
            <button
              type="button"
              onClick={actions.onCancel}
              className={
                backOnly
                  ? `${cancelClass} w-full`
                  : backAndSkipOnly
                    ? cancelClassPair
                    : `${cancelClass} min-w-[5.5rem]`
              }
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0"
                aria-hidden
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
              <span className="whitespace-nowrap">{_(msg`Back`)}</span>
            </button>
          )}
          {showContinue && (
            <button
              type="button"
              onClick={actions.onContinue}
              disabled={actions.continueDisabled || actions.continueLoading}
              className={
                continueOnly
                  ? `${continueClass} w-full`
                  : `${continueClass} min-w-0 flex-1`
              }
            >
              {actions.continueLoading ? (
                <>
                  <span className="h-4 w-4 shrink-0 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  <span className="whitespace-nowrap">{_(msg`Please wait…`)}</span>
                </>
              ) : (
                <>
                  <span className="whitespace-nowrap">
                    {actions.continueLabel ?? _(msg`Continue`)}
                  </span>
                  {actions.continueShowKeyboardHint !== false ? (
                    <span className="text-white/70 text-xs font-normal leading-none">
                      {"\u21B5"}
                    </span>
                  ) : null}
                </>
              )}
            </button>
          )}
          {showSkip && (
            <button
              type="button"
              onClick={actions.onSkip}
              className={
                skipOnly
                  ? `${skipClass} w-full`
                  : backAndSkipOnly
                    ? skipClassPair
                    : skipClass
              }
            >
              <span className="whitespace-nowrap">{_(msg`Skip`)}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0"
                aria-hidden
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function CardShell({
  children,
  animDir,
  progressPercent,
  currentIndex,
  totalCards: _totalCards,
  isWide,
  verticallyCenterContent = false,
  onNext: _onNext,
  onBack: _onBack,
  cardKey,
}: CardShellProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const prevIndexRef = useRef<number | null>(null);
  const [mobileChrome, setMobileChrome] = useState<IntakeMobileChromeActions | null>(
    null,
  );

  // Scroll the card viewport to the top when moving forward (next / skip / jump ahead)
  useEffect(() => {
    const prev = prevIndexRef.current;
    prevIndexRef.current = currentIndex;
    if (prev === null) return;
    if (currentIndex <= prev) return;
    const el = scrollAreaRef.current;
    if (!el) return;
    const scrollToTop = () => {
      el.scrollTo({ top: 0, left: 0, behavior: "auto" });
    };
    scrollToTop();
    requestAnimationFrame(scrollToTop);
  }, [currentIndex]);

  // Keep focus inside the card for keyboard users
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const firstInput = el.querySelector<HTMLElement>(
      "input, button, select, textarea, [tabindex]:not([tabindex='-1'])",
    );
    firstInput?.focus({ preventScroll: true });
  }, [cardKey]);

  const animClass =
    animDir === "forward"
      ? "card-enter-forward"
      : animDir === "backward"
        ? "card-enter-backward"
        : "";

  const mainJustify = verticallyCenterContent
    ? "justify-center"
    : "justify-start md:justify-center";

  const scrollPadMobile = !!mobileChrome;

  return (
    <div className="fixed inset-0 h-[100dvh] flex flex-col bg-[#F7F7F5] overflow-hidden">
      {/* ── Top bar: logo + progress ──────────────────────────────────────── */}
      <div className="flex-shrink-0 border-b border-gray-200/80 bg-white px-4 pb-2.5 pt-2.5 sm:px-8 sm:pt-3 sm:pb-3">
        {/* Logo row */}
        <div className="mb-2 flex items-center justify-between sm:mb-2.5">
          <a href="/" className="flex-shrink-0">
            <img
              src="/Icon_Jema.svg"
              alt="Jema Services"
              className="h-6 w-auto object-contain opacity-95 sm:h-7"
            />
          </a>
        </div>

        {/* Progress bar */}
        <div className="h-[3px] w-full overflow-hidden rounded-full bg-gray-200/90">
          <div
            className="progress-fill h-full rounded-full"
            style={{
              width: `${progressPercent}%`,
              background: "linear-gradient(90deg, #1a7faf, #166B93)",
              boxShadow: "0 0 6px rgba(22,107,147,0.35)",
            }}
          />
        </div>
      </div>

      <IntakeMobileChromeSetterProvider value={setMobileChrome}>
        {/* ── Card content area ─────────────────────────────────────────────── */}
        <div
          ref={scrollAreaRef}
          className={`flex-1 overflow-y-auto flex flex-col ${scrollPadMobile ? "max-md:pb-44" : ""}`}
        >
          <div
            key={cardKey}
            ref={containerRef}
            className={`flex-1 flex flex-col ${mainJustify} w-full mx-auto py-4 md:py-6 ${animClass} ${
              isWide ? "max-w-5xl px-3 sm:px-6" : "max-w-2xl px-4 sm:px-12"
            }`}
          >
            {children}
          </div>
        </div>

        <IntakeMobileStickyFooter actions={mobileChrome} />
      </IntakeMobileChromeSetterProvider>
    </div>
  );
}
